/**
 * Item catalog, equipment, recipes, and inventory types.
 * Run bag is wiped on death; bank + owned gear persist.
 */

import { BlockType, PickaxeTier } from '../types';

export enum ItemId {
  // Materials
  Dirt = 'dirt',
  Stone = 'stone',
  IronOre = 'iron_ore',
  GoldOre = 'gold_ore',

  // Tools
  PickaxeRusty = 'pickaxe_rusty',
  PickaxeIron = 'pickaxe_iron',
  PickaxeSteel = 'pickaxe_steel',
  PickaxeCarbide = 'pickaxe_carbide',
  PickaxePlasma = 'pickaxe_plasma',

  // Weapons
  SwordRusty = 'sword_rusty',
  SwordIron = 'sword_iron',
  SwordSteel = 'sword_steel',
  MaceIron = 'mace_iron',
  BlasterPlasma = 'blaster_plasma',
}

export type ItemCategory = 'material' | 'tool' | 'weapon';

export interface ItemDef {
  id: ItemId;
  name: string;
  category: ItemCategory;
  description: string;
  /** Max stack size (1 for unique gear). */
  maxStack: number;
  /** Mining power 0–4; gates which blocks can be broken. */
  minePower: number;
  /**
   * Work applied per second while holding mine.
   * Time-to-break ≈ BLOCK_HARDNESS / mineSpeed.
   */
  mineSpeed?: number;
  /** Melee / projectile damage for combat. */
  damage: number;
  /** Attack / mine cooldown override in ms (0 = use config default). */
  cooldownMs: number;
  /** Reach override in world units (0 = use config default). */
  reach: number;
  /** Visual / hotbar accent. */
  color: string;
  /** Credits paid per unit when sold at a floor shop (materials only). */
  sellPrice?: number;
  /** Linked pickaxe tier when this is a pickaxe. */
  pickaxeTier?: PickaxeTier;
}

export interface RecipeCost {
  itemId: ItemId;
  amount: number;
}

export interface Recipe {
  id: string;
  name: string;
  description: string;
  /** Result granted (and auto-unlocked/equipped for gear). */
  result: ItemId;
  /** Amount of result for stackables; gear is always 1. */
  resultAmount: number;
  costs: readonly RecipeCost[];
  /** Optional credit cost from wallet. */
  creditCost: number;
  /** Must already own this item to craft the upgrade (gear ladders). */
  requiresOwned?: ItemId;
  /** Only craftable while in BaseCamp. (Unused — forge works anywhere.) */
  campOnly: boolean;
}

export interface InventorySlot {
  itemId: ItemId;
  amount: number;
}

export interface HotbarState {
  /** Up to 5 equipped-action slots (tools/weapons the player owns). */
  slots: Array<ItemId | null>;
  activeIndex: number;
}

/** Minimum minePower required to chip each block. */
export const BLOCK_MINE_POWER: Record<number, number> = {
  [BlockType.Dirt]: 0,
  [BlockType.Stone]: 0,
  [BlockType.Iron]: 1,
  [BlockType.Gold]: 2,
};

/**
 * Work units to break a block at mineSpeed 1.0.
 * Rusty (1.0): dirt ~0.45s, stone ~1.4s, iron ~2.8s
 * Iron pick (1.7): gold ~3.2s, iron ~1.6s
 * Plasma (4.2): stone ~0.33s, gold ~1.3s
 */
export const BLOCK_HARDNESS: Record<number, number> = {
  [BlockType.Dirt]: 0.45,
  [BlockType.Stone]: 1.4,
  [BlockType.Iron]: 2.8,
  [BlockType.Gold]: 5.5,
};

export const ITEM_CATALOG: Record<ItemId, ItemDef> = {
  [ItemId.Dirt]: {
    id: ItemId.Dirt,
    name: 'Dirt',
    category: 'material',
    description: 'Loose soil. Weak filler for early crafts.',
    maxStack: 999,
    minePower: 0,
    damage: 0,
    cooldownMs: 0,
    reach: 0,
    color: '#8a6238',
    sellPrice: 0,
  },
  [ItemId.Stone]: {
    id: ItemId.Stone,
    name: 'Stone',
    category: 'material',
    description: 'Common rock. Backbone of most recipes.',
    maxStack: 999,
    minePower: 0,
    damage: 0,
    cooldownMs: 0,
    reach: 0,
    color: '#8a929c',
    sellPrice: 1,
  },
  [ItemId.IronOre]: {
    id: ItemId.IronOre,
    name: 'Iron Ore',
    category: 'material',
    description: 'Dense metal ore. Used for tools and blades.',
    maxStack: 999,
    minePower: 0,
    damage: 0,
    cooldownMs: 0,
    reach: 0,
    color: '#b8c6d8',
    sellPrice: 5,
  },
  [ItemId.GoldOre]: {
    id: ItemId.GoldOre,
    name: 'Gold Ore',
    category: 'material',
    description: 'Rare soft metal. Powers advanced upgrades.',
    maxStack: 999,
    minePower: 0,
    damage: 0,
    cooldownMs: 0,
    reach: 0,
    color: '#f0c43a',
    sellPrice: 20,
  },

  [ItemId.PickaxeRusty]: {
    id: ItemId.PickaxeRusty,
    name: 'Rusty Pickaxe',
    category: 'tool',
    description: 'Starter digger. Slow on stone and iron.',
    maxStack: 1,
    minePower: 1,
    mineSpeed: 1.0,
    damage: 4,
    cooldownMs: 180,
    reach: 5,
    color: '#8a5a3a',
    pickaxeTier: PickaxeTier.Rusty,
  },
  [ItemId.PickaxeIron]: {
    id: ItemId.PickaxeIron,
    name: 'Iron Pickaxe',
    category: 'tool',
    description: 'Mines gold. Digs ~70% faster than rusty.',
    maxStack: 1,
    minePower: 2,
    mineSpeed: 1.7,
    damage: 6,
    cooldownMs: 160,
    reach: 5.2,
    color: '#b0bcc8',
    pickaxeTier: PickaxeTier.Iron,
  },
  [ItemId.PickaxeSteel]: {
    id: ItemId.PickaxeSteel,
    name: 'Steel Pickaxe',
    category: 'tool',
    description: 'Hardened steel. Fast digs, longer reach.',
    maxStack: 1,
    minePower: 3,
    mineSpeed: 2.4,
    damage: 8,
    cooldownMs: 140,
    reach: 5.4,
    color: '#d0d6de',
    pickaxeTier: PickaxeTier.Steel,
  },
  [ItemId.PickaxeCarbide]: {
    id: ItemId.PickaxeCarbide,
    name: 'Carbide Pickaxe',
    category: 'tool',
    description: 'Industrial tip. Very fast dig speed.',
    maxStack: 1,
    minePower: 3,
    mineSpeed: 3.2,
    damage: 10,
    cooldownMs: 110,
    reach: 5.8,
    color: '#6ec8ff',
    pickaxeTier: PickaxeTier.Carbide,
  },
  [ItemId.PickaxePlasma]: {
    id: ItemId.PickaxePlasma,
    name: 'Plasma Pickaxe',
    category: 'tool',
    description: 'Peak digger. Melts through rock.',
    maxStack: 1,
    minePower: 4,
    mineSpeed: 4.2,
    damage: 14,
    cooldownMs: 90,
    reach: 6.2,
    color: '#b388ff',
    pickaxeTier: PickaxeTier.Plasma,
  },

  [ItemId.SwordRusty]: {
    id: ItemId.SwordRusty,
    name: 'Rusty Sword',
    category: 'weapon',
    description: 'Light blade. Fast swings, weak vs rock.',
    maxStack: 1,
    minePower: 0,
    damage: 10,
    cooldownMs: 220,
    reach: 3.2,
    color: '#a07050',
  },
  [ItemId.SwordIron]: {
    id: ItemId.SwordIron,
    name: 'Iron Sword',
    category: 'weapon',
    description: 'Reliable sidearm. Solid melee damage.',
    maxStack: 1,
    minePower: 0,
    damage: 16,
    cooldownMs: 200,
    reach: 3.4,
    color: '#c0cad4',
  },
  [ItemId.SwordSteel]: {
    id: ItemId.SwordSteel,
    name: 'Steel Sword',
    category: 'weapon',
    description: 'Sharp and balanced. High burst damage.',
    maxStack: 1,
    minePower: 0,
    damage: 24,
    cooldownMs: 180,
    reach: 3.6,
    color: '#e8eef5',
  },
  [ItemId.MaceIron]: {
    id: ItemId.MaceIron,
    name: 'Iron Mace',
    category: 'weapon',
    description: 'Crushing head. Can smash dirt and stone.',
    maxStack: 1,
    minePower: 0,
    damage: 20,
    cooldownMs: 320,
    reach: 3.0,
    color: '#7a8490',
  },
  [ItemId.BlasterPlasma]: {
    id: ItemId.BlasterPlasma,
    name: 'Plasma Blaster',
    category: 'weapon',
    description: 'Ranged bolt. High damage, long reach.',
    maxStack: 1,
    minePower: 0,
    damage: 28,
    cooldownMs: 280,
    reach: 12,
    color: '#c77dff',
  },
};

export const CRAFTING_RECIPES: readonly Recipe[] = [
  // Pickaxe upgrades
  {
    id: 'upgrade_pick_iron',
    name: 'Forge Iron Pickaxe',
    description: 'Upgrade your rusty pick — unlocks gold ore mining.',
    result: ItemId.PickaxeIron,
    resultAmount: 1,
    costs: [
      { itemId: ItemId.IronOre, amount: 12 },
      { itemId: ItemId.Stone, amount: 20 },
    ],
    creditCost: 0,
    requiresOwned: ItemId.PickaxeRusty,
    campOnly: false,
  },
  {
    id: 'upgrade_pick_steel',
    name: 'Forge Steel Pickaxe',
    description: 'Temper an iron pick into steel.',
    result: ItemId.PickaxeSteel,
    resultAmount: 1,
    costs: [
      { itemId: ItemId.IronOre, amount: 24 },
      { itemId: ItemId.GoldOre, amount: 4 },
      { itemId: ItemId.Stone, amount: 30 },
    ],
    creditCost: 40,
    requiresOwned: ItemId.PickaxeIron,
    campOnly: false,
  },
  {
    id: 'upgrade_pick_carbide',
    name: 'Forge Carbide Pickaxe',
    description: 'Industrial carbide tip for deep veins.',
    result: ItemId.PickaxeCarbide,
    resultAmount: 1,
    costs: [
      { itemId: ItemId.IronOre, amount: 40 },
      { itemId: ItemId.GoldOre, amount: 12 },
    ],
    creditCost: 120,
    requiresOwned: ItemId.PickaxeSteel,
    campOnly: false,
  },
  {
    id: 'upgrade_pick_plasma',
    name: 'Forge Plasma Pickaxe',
    description: 'Experimental plasma cutter. Endgame digger.',
    result: ItemId.PickaxePlasma,
    resultAmount: 1,
    costs: [
      { itemId: ItemId.GoldOre, amount: 30 },
      { itemId: ItemId.IronOre, amount: 50 },
    ],
    creditCost: 300,
    requiresOwned: ItemId.PickaxeCarbide,
    campOnly: false,
  },

  // Weapons
  {
    id: 'craft_sword_rusty',
    name: 'Craft Rusty Sword',
    description: 'A scrap blade for close quarters.',
    result: ItemId.SwordRusty,
    resultAmount: 1,
    costs: [
      { itemId: ItemId.Stone, amount: 15 },
      { itemId: ItemId.Dirt, amount: 10 },
    ],
    creditCost: 0,
    campOnly: false,
  },
  {
    id: 'craft_sword_iron',
    name: 'Craft Iron Sword',
    description: 'Proper forged blade.',
    result: ItemId.SwordIron,
    resultAmount: 1,
    costs: [
      { itemId: ItemId.IronOre, amount: 16 },
      { itemId: ItemId.Stone, amount: 10 },
    ],
    creditCost: 20,
    requiresOwned: ItemId.SwordRusty,
    campOnly: false,
  },
  {
    id: 'craft_sword_steel',
    name: 'Craft Steel Sword',
    description: 'High-grade cutting edge.',
    result: ItemId.SwordSteel,
    resultAmount: 1,
    costs: [
      { itemId: ItemId.IronOre, amount: 28 },
      { itemId: ItemId.GoldOre, amount: 6 },
    ],
    creditCost: 80,
    requiresOwned: ItemId.SwordIron,
    campOnly: false,
  },
  {
    id: 'craft_mace_iron',
    name: 'Craft Iron Mace',
    description: 'Heavy crusher for combat.',
    result: ItemId.MaceIron,
    resultAmount: 1,
    costs: [
      { itemId: ItemId.IronOre, amount: 20 },
      { itemId: ItemId.Stone, amount: 25 },
    ],
    creditCost: 30,
    campOnly: false,
  },
  {
    id: 'craft_blaster_plasma',
    name: 'Craft Plasma Blaster',
    description: 'Long-range energy weapon.',
    result: ItemId.BlasterPlasma,
    resultAmount: 1,
    costs: [
      { itemId: ItemId.GoldOre, amount: 20 },
      { itemId: ItemId.IronOre, amount: 35 },
    ],
    creditCost: 200,
    requiresOwned: ItemId.SwordSteel,
    campOnly: false,
  },
];

export function getItemDef(id: ItemId): ItemDef {
  return ITEM_CATALOG[id];
}

/** Credits paid per unit at a floor shop (0 = not sold / worthless). */
export function getSellPrice(id: ItemId): number {
  return ITEM_CATALOG[id].sellPrice ?? 0;
}

export function blockTypeToItem(type: BlockType): ItemId | null {
  switch (type) {
    case BlockType.Dirt:
      return ItemId.Dirt;
    case BlockType.Stone:
      return ItemId.Stone;
    case BlockType.Iron:
      return ItemId.IronOre;
    case BlockType.Gold:
      return ItemId.GoldOre;
    default:
      return null;
  }
}

export function pickaxeTierToItem(tier: PickaxeTier): ItemId {
  switch (tier) {
    case PickaxeTier.Iron:
      return ItemId.PickaxeIron;
    case PickaxeTier.Steel:
      return ItemId.PickaxeSteel;
    case PickaxeTier.Carbide:
      return ItemId.PickaxeCarbide;
    case PickaxeTier.Plasma:
      return ItemId.PickaxePlasma;
    case PickaxeTier.Rusty:
    default:
      return ItemId.PickaxeRusty;
  }
}

export function isGear(id: ItemId): boolean {
  const cat = ITEM_CATALOG[id].category;
  return cat === 'tool' || cat === 'weapon';
}
